import React, { useEffect, useState } from "react";
import { useTheme } from "@/context/ThemeContext";

export const CelestialToggle = () => {
  const { theme, toggle } = useTheme();
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // The hero scrub pins the page for 200vh.
      // Once scrollY is past ~1.8vh, we dock the celestial button.
      setDocked(window.scrollY > window.innerHeight * 1.8);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Run once on load
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isDay = theme === "day";

  return (
    <button
      type="button"
      data-docked={docked}
      data-ready="true"
      aria-label={isDay ? "Switch to night theme" : "Switch to light theme"}
      aria-pressed={!isDay}
      title={isDay ? "Switch to night theme" : "Switch to light theme"}
      onClick={toggle}
      className="celestial-toggle group fixed right-[var(--chrome-gutter-edge)] top-4 z-[60] grid place-items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[6px] focus-visible:outline-peach-300 md:top-5 w-12 h-12 md:w-[50px] md:h-[50px] cursor-pointer"
      style={{
        background: "transparent",
        border: "none",
        outline: "none",
      }}
    >
      {/* Docked Background */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full border transition-opacity duration-500 group-hover:border-peach-300/55"
        style={{
          borderColor: "var(--theme-toggle-border)",
          background: "var(--theme-toggle-bg)",
          boxShadow: "var(--theme-toggle-shadow)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          opacity: docked ? 1 : 0,
        }}
      />

      {/* Ping Animation Indicator */}
      <span
        aria-hidden="true"
        className="celestial-ping pointer-events-none absolute inset-[14%] rounded-full border border-peach-300/45"
      />

      {/* Rotating / Scaling Icon Container */}
      <span className="relative grid h-full w-full place-items-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06] group-active:scale-95">
        <svg
          viewBox="0 0 100 100"
          className="h-full w-full overflow-visible"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <radialGradient id="csun-R4" cx="50%" cy="42%" r="62%">
              <stop offset="0%" stopColor="#fff7e8" />
              <stop offset="44%" stopColor="#ffd27a" />
              <stop offset="100%" stopColor="#f2a046" />
            </radialGradient>
            <radialGradient id="csunh-R4" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,206,128,0.55)" />
              <stop offset="55%" stopColor="rgba(255,176,92,0.16)" />
              <stop offset="100%" stopColor="rgba(255,176,92,0)" />
            </radialGradient>
            <radialGradient id="cmoon-R4" cx="38%" cy="36%" r="72%">
              <stop offset="0%" stopColor="#fbf8f1" />
              <stop offset="58%" stopColor="#e7e6df" />
              <stop offset="100%" stopColor="#c6cad4" />
            </radialGradient>
            <radialGradient id="cmoonh-R4" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(205,216,242,0.5)" />
              <stop offset="55%" stopColor="rgba(158,176,222,0.16)" />
              <stop offset="100%" stopColor="rgba(158,176,222,0)" />
            </radialGradient>
          </defs>

          {/* Sun Halo */}
          <circle
            cx="50"
            cy="50"
            r="49"
            fill="url(#csunh-R4)"
            style={{
              transformBox: "fill-box",
              transformOrigin: "center center",
              transition: "opacity 0.7s, transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)",
              opacity: isDay ? 1 : 0,
              transform: isDay ? "none" : "scale(0.7)",
            }}
          />

          {/* Moon Halo */}
          <circle
            cx="50"
            cy="50"
            r="49"
            fill="url(#cmoonh-R4)"
            style={{
              transformBox: "fill-box",
              transformOrigin: "center center",
              transition: "opacity 0.7s, transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)",
              opacity: !isDay ? 1 : 0,
              transform: !isDay ? "none" : "scale(0.7)",
            }}
          />

          {/* Night Mode Stars */}
          <g
            style={{
              opacity: !isDay ? 1 : 0,
              transition: "opacity 0.7s, transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)",
            }}
          >
            <circle className="np-star" cx="20" cy="28" r="1.5" fill="#fbf8f1" />
            <circle className="np-star" cx="82" cy="24" r="1.1" fill="#fbf8f1" style={{ animationDelay: "0.7s" }} />
            <circle className="np-star" cx="84" cy="66" r="1" fill="#fbf8f1" style={{ animationDelay: "1.2s" }} />
          </g>

          {/* Sun Body & Rays */}
          <g
            style={{
              transformBox: "fill-box",
              transformOrigin: "center center",
              transition: "opacity 0.7s, transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)",
              opacity: isDay ? 1 : 0,
              transform: isDay ? "none" : "scale(0.5) rotate(-45deg)",
            }}
          >
            <g className="hero-sun-rays">
              <line x1="74" y1="50" x2="81" y2="50" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="70.78460969082653" y1="62" x2="76.84678751731761" y2="65.5" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="62" y1="70.78460969082653" x2="65.5" y2="76.8467875173176" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="50" y1="74" x2="50" y2="81" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="38" y1="70.78460969082653" x2="34.5" y2="76.84678751731761" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="29.215390309173472" y1="62" x2="23.1532124826824" y2="65.5" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="26" y1="50" x2="19" y2="50" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="29.21539030917347" y1="38" x2="23.153212482682395" y2="34.5" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="38" y1="29.215390309173475" x2="34.5" y2="23.153212482682406" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="50" y1="26" x2="50" y2="19" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="62" y1="29.215390309173472" x2="65.5" y2="23.153212482682402" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
              <line x1="70.78460969082653" y1="38" x2="76.8467875173176" y2="34.5" stroke="#ffce80" strokeWidth="2.4" strokeLinecap="round" opacity="0.92" />
            </g>
            <circle cx="50" cy="50" r="16" fill="url(#csun-R4)" />
          </g>

          {/* Moon Body & Craters */}
          <g
            style={{
              transformBox: "fill-box",
              transformOrigin: "center center",
              transition: "opacity 0.7s, transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)",
              opacity: !isDay ? 1 : 0,
              transform: !isDay ? "none" : "scale(0.5) rotate(45deg)",
            }}
          >
            <circle cx="50" cy="50" r="16" fill="url(#cmoon-R4)" />
            <circle cx="44" cy="45" r="3.1" fill="rgba(150,160,186,0.34)" />
            <circle cx="56.5" cy="52" r="2.1" fill="rgba(150,160,186,0.3)" />
            <circle cx="48.5" cy="57.5" r="1.5" fill="rgba(150,160,186,0.28)" />
          </g>
        </svg>
      </span>

      {/* Day / Night Text Label */}
      <span
        aria-hidden="true"
        className="pointer-events-auto absolute left-1/2 top-full mt-2 flex -translate-x-1/2 cursor-pointer items-center gap-1 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.28em] transition-opacity duration-500 md:text-[10px]"
        style={{
          textShadow: "var(--hero-body-shadow)",
          opacity: docked ? 0 : 1,
          pointerEvents: docked ? "none" : "auto",
        }}
      >
        <span style={{ color: "var(--hero-meta-text)", opacity: isDay ? 1 : 0.42, transition: "opacity 300ms" }}>
          Day
        </span>
        <span style={{ color: "var(--hero-soft-text)", opacity: 0.5 }}>/</span>
        <span style={{ color: "var(--hero-meta-text)", opacity: !isDay ? 1 : 0.42, transition: "opacity 300ms" }}>
          Night
        </span>
      </span>
    </button>
  );
};

export default CelestialToggle;
